import React, { useMemo } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { useAuth } from '@affiliate-cabinet/auth'
import { useI18n } from '@affiliate-cabinet/i18n'
import { CreditCardDown13 } from '@affstore-ds/icons'
import { Divider, Group, mediaHelper, Money, NavLink, rem, Skeleton, Stack, Text, useMediaQuery, useNavbarState } from '@affstore-ds/uikit'

import { featuresStatuses } from '../../config/brands'
import { useBalance } from '../../entities/balance'
import { LevelBadge } from '../../entities/loyalty'
import { LanguageDropdown } from '../../features/LanguageDropdown'
import { profileTabSettings } from '../../shared/navigation/NavMenuData'
import { routes } from '../../shared/routes'
import { PaymentInfo } from './containers/PaymentInfo'
import { headerMainTranslationKeys } from './translations/headerMain.translationKeys'

import { useFirstLastName } from './hooks/useFirstLastName'

import { useHeaderMainStyles } from './HeaderMain.styled'

export const HeaderMain = () => {
  const { i18n } = useI18n()

  const {
    auth: { user },
  } = useAuth()

  const { balanceData, balanceIsFetching } = useBalance()

  const { firstName, lastName } = useFirstLastName(user?.legal_profile)

  const isGreaterThenSm = useMediaQuery(mediaHelper.greaterThen('sm', false))

  const { closeMobileNavbar, isNavbarOpen } = useNavbarState()

  const { classes } = useHeaderMainStyles()

  const renderBalance = useMemo(() => {
    if (balanceIsFetching) {
      return (
        <Stack gap={12}>
          <Skeleton width={89} height={12} />
          <Skeleton width={89} height={12} />
        </Stack>
      )
    }

    return (
      <Stack className={classes.balanceStack} gap={0}>
        <Text className={classes.balanceWrapper} colorVariant="greyIron.18">
          <Money c="inherit" currency={balanceData.currency} value={balanceData.amount} inherit />
        </Text>
        {balanceData.availableAmount !== balanceData.amount && (
          <Text colorVariant="froggy.12" display="flex" elementSize="xs" elementVariant="buttons">
            <CreditCardDown13 width={16} height={16} style={{ marginInlineEnd: 4 }} />
            <Money c="inherit" currency={balanceData.availableCurrency} value={balanceData.availableAmount} inherit />
            &nbsp;{i18n.t(headerMainTranslationKeys.balanceAvailable)}
          </Text>
        )}
      </Stack>
    )
  }, [balanceIsFetching, balanceData, i18n, classes])

  const renderMiddleSection = useMemo(() => {
    if (isGreaterThenSm) {
      return renderBalance
    }

    return isNavbarOpen ? (
      <Group
        align="center"
        className={classes.mobileControls}
        justify={featuresStatuses.isLoyaltyProgramPageEnabled ? 'space-between' : 'flex-end'}
        w={`calc(100% + ${rem(16)})`}
      >
        <LanguageDropdown type="cabinet" />
      </Group>
    ) : (
      renderBalance
    )
  }, [isNavbarOpen, isGreaterThenSm, renderBalance, classes.mobileControls])

  return (
    <Group wrap="nowrap">
      {isGreaterThenSm && <LanguageDropdown type="cabinet" />}
      {featuresStatuses.isLoyaltyProgramPageEnabled && !isNavbarOpen && (
        <Group hiddenFrom="sm" className={classes.loyaltyBadgeMobile} flex="1 0 auto">
          <LevelBadge />
        </Group>
      )}
      <Group align="center" className={classes.headerControls} gap={16} w={{ base: '100%', sm: 'auto' }} wrap="nowrap">
        {user?.is_onboarding_passed && (
          <Group visibleFrom="md">
            <PaymentInfo />
            <Divider className={classes.divider} m="auto" orientation="vertical" />
          </Group>
        )}
        {featuresStatuses.isLoyaltyProgramPageEnabled && (
          <Group visibleFrom="sm">
            <LevelBadge />
            <Divider className={classes.divider} m="auto" orientation="vertical" />
          </Group>
        )}
        {renderMiddleSection}
        {isGreaterThenSm && (
          <NavLink
            label={
              <Group gap={16}>
                {(firstName || lastName) && (
                  <Stack gap={0}>
                    {firstName && (
                      <Text colorVariant="greyIron.18" component="bdi" elementSize="s" elementVariant="important" ta="end">
                        {firstName}
                      </Text>
                    )}
                    {lastName && (
                      <Text colorVariant="greyIron.18" component="bdi" elementSize="s" elementVariant="important" ta="end">
                        {lastName}
                      </Text>
                    )}
                  </Stack>
                )}
              </Group>
            }
            active={false}
            component={RouterLink}
            disabled={!user?.is_onboarding_passed}
            display="inline-flex"
            mx={-12}
            onClick={closeMobileNavbar}
            to={routes.ROUTES_PROFILE.profileSettingsTab.replace(':tabValue', profileTabSettings.personal)}
            w="auto"
          />
        )}
      </Group>
    </Group>
  )
}
